import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import client, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function VendorApply() {
  const { user, refreshUser } = useAuth() || {};
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", slug: "", tagline: "", description: "",
    banner_image: "", logo_image: "", hechsher: "",
  });
  const [loading, setLoading] = useState(false);

  if (!user) {
    return (
      <div className="container-editorial mt-14 py-20 text-center">
        <p className="label-uppercase text-[#B24C38]">Sell on Kesher</p>
        <h1 className="font-serif text-5xl tracking-tighter mt-3">Sign in to apply.</h1>
        <button className="btn-terracotta mt-8" onClick={() => navigate("/login?redirect=/vendor/apply")}>Sign In →</button>
      </div>
    );
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v, ...(k === "name" ? { slug: slugify(v) } : {}) }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await client.post("/vendor/apply", { ...form, slug: form.slug || slugify(form.name) });
      if (refreshUser) await refreshUser();
      toast.success("Storefront created! Welcome to Kesher.");
      navigate("/vendor/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Application failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="container-editorial mt-8 md:mt-14">
      <div className="border-b border-[#EAE5D9] pb-10 mb-12">
        <p className="label-uppercase text-[#B24C38]">Vendor onboarding</p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter mt-3">
          Open your storefront.
        </h1>
        <p className="text-[#4A4A4A] mt-4 max-w-xl">
          Kesher is Jewish-owned by design. Tell us about your atelier and start listing today.
        </p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-8 space-y-8">
          <div>
            <label className="label-uppercase block mb-2">Boutique name</label>
            <Input required value={form.name} onChange={e => set("name", e.target.value)}
                   data-testid="vendor-apply-name"
                   className="rounded-none border-[#111] bg-transparent h-12"/>
          </div>
          <div>
            <label className="label-uppercase block mb-2">URL slug</label>
            <Input required value={form.slug} onChange={e => set("slug", slugify(e.target.value))}
                   data-testid="vendor-apply-slug"
                   className="rounded-none border-[#111] bg-transparent h-12"/>
            <p className="text-xs text-[#4A4A4A] mt-1">kesher.shop/vendor/{form.slug || "your-boutique"}</p>
          </div>
          <div>
            <label className="label-uppercase block mb-2">Tagline</label>
            <Input required maxLength={80} value={form.tagline} onChange={e => set("tagline", e.target.value)}
                   data-testid="vendor-apply-tagline"
                   className="rounded-none border-[#111] bg-transparent h-12"/>
          </div>
          <div>
            <label className="label-uppercase block mb-2">Story</label>
            <Textarea required rows={5} value={form.description} onChange={e => set("description", e.target.value)}
                      data-testid="vendor-apply-description"
                      className="rounded-none border-[#111] bg-transparent"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-uppercase block mb-2">Banner image URL</label>
              <Input required type="url" value={form.banner_image} onChange={e => set("banner_image", e.target.value)}
                     placeholder="https://…"
                     data-testid="vendor-apply-banner"
                     className="rounded-none border-[#111] bg-transparent h-12"/>
            </div>
            <div>
              <label className="label-uppercase block mb-2">Logo image URL</label>
              <Input required type="url" value={form.logo_image} onChange={e => set("logo_image", e.target.value)}
                     placeholder="https://…"
                     data-testid="vendor-apply-logo"
                     className="rounded-none border-[#111] bg-transparent h-12"/>
            </div>
          </div>
          <div>
            <label className="label-uppercase block mb-2">Certification / hechsher (optional)</label>
            <Input value={form.hechsher} onChange={e => set("hechsher", e.target.value)}
                   placeholder="e.g., Shomer Shabbos Boutique"
                   data-testid="vendor-apply-hechsher"
                   className="rounded-none border-[#111] bg-transparent h-12"/>
          </div>
          <button type="submit" disabled={loading} className="btn-terracotta w-full md:w-auto" data-testid="vendor-apply-submit">
            {loading ? "Submitting…" : "Open my storefront →"}
          </button>
        </div>
        <aside className="col-span-12 md:col-span-4 bg-[#EAE5D9] p-8 h-max sticky top-24">
          <p className="label-uppercase text-[#B24C38]">What you get</p>
          <ul className="mt-4 space-y-4 font-serif text-lg leading-tight">
            <li>· Your own storefront page</li>
            <li>· Product uploads with sizes & colors</li>
            <li>· Real-time order dashboard</li>
            <li>· Payouts via Stripe Connect (coming)</li>
            <li>· Featured in editorial rotations</li>
          </ul>
          <p className="text-xs text-[#4A4A4A] mt-8">Approval is instant for launch. Verification badges reviewed within 5 business days.</p>
        </aside>
      </form>
    </div>
  );
}
